import { HttpError } from '@littlebird/auth-client';
import { extractBackendErrorCode } from '@littlebird/sdk';
import {
  buildSendDraftFromLine,
  classifyEmailDraftError,
  draftToEditForm,
  editFormToUpdatePayload,
  type EditForm,
} from '@littlebird/ui-utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import {
  useEmailDraftQuery,
  useSendDraftMutation,
  useUpdateDraftMutation,
} from '@/features/integrations/integrations.query';
import { getIntegrationIcon } from '@/integrations/integration-icons';
import { runFrames } from '@/lib/animate';
import { trackEvent } from '@/utils/analytics';

export type SendState = 'idle' | 'sending' | 'sent';
export type DraftLoadState = 'loading' | 'ready' | 'error';

export interface UseSendDraftCardArgs {
  draftId: string;
  toolkitName: string;
  accountLabel?: string;
  defaultEditing?: boolean;
  onSent?: () => void;
  onSaved?: () => void;
  onEditCancelled?: () => void;
}

export function useSendDraftCard({
  draftId,
  toolkitName,
  accountLabel = '',
  defaultEditing = false,
  onSent,
  onSaved,
  onEditCancelled,
}: UseSendDraftCardArgs) {
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [sendState, setSendState] = useState<SendState>('idle');
  const editFooterRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!isEditing) return;
    return runFrames(
      10,
      () => {
        editFooterRef.current?.scrollIntoView({
          behavior: 'auto',
          block: 'nearest',
        });
      },
      () => {}
    );
  }, [isEditing]);

  const draftQuery = useEmailDraftQuery(draftId, toolkitName, accountLabel);
  const updateDraft = useUpdateDraftMutation();
  const sendDraft = useSendDraftMutation();

  const draft = draftQuery.data ?? null;
  const loadError =
    draftQuery.error instanceof HttpError ? draftQuery.error : null;
  const loadState: DraftLoadState = draftQuery.isError
    ? 'error'
    : draftQuery.isLoading
      ? 'loading'
      : 'ready';
  const errorCode = loadError
    ? classifyEmailDraftError({
        message: loadError.message,
        errorCode: extractBackendErrorCode(loadError.body),
      })
    : 'unknown';
  const isUnavailable = errorCode === 'draft_not_found';
  const isAccountNotFound = errorCode === 'account_not_found';
  const isSaving = updateDraft.isPending;

  const IntegrationIcon = getIntegrationIcon(toolkitName);
  const fromLine = buildSendDraftFromLine(accountLabel, toolkitName);

  const draftIsFresh = !draftQuery.isFetching && !draftQuery.isError;

  const isAwaitingAutoEdit =
    defaultEditing && !isEditing && loadState !== 'error';

  const autoEditAppliedRef = useRef(false);
  useEffect(() => {
    if (!defaultEditing || autoEditAppliedRef.current) return;
    if (!draft || !draftIsFresh) return;
    autoEditAppliedRef.current = true;
    setEditForm(draftToEditForm(draft));
    setIsEditing(true);
  }, [defaultEditing, draft, draftIsFresh]);

  const handleStartEdit = useCallback(() => {
    if (!draft || !draftIsFresh) return;
    setEditForm(draftToEditForm(draft));
    setShowCcBcc(false);
    setIsEditing(true);
    trackEvent('EMAIL_EDIT_VIA_INTEGRATION', {
      toolkit_name: toolkitName,
    });
  }, [draft, draftIsFresh, toolkitName]);

  const handleCancelEdit = useCallback(async () => {
    const isDirty =
      !!draft &&
      !!editForm &&
      JSON.stringify(editForm) !== JSON.stringify(draftToEditForm(draft));
    if (isDirty) {
      const confirmed = await ModalsManager.showResolvable(ConfirmDialog, {
        title: 'Discard changes?',
        description:
          'Your edits to this draft will be lost. The original draft is kept as it was.',
        confirmText: 'Discard',
      });
      if (!confirmed) return;
    }
    setIsEditing(false);
    setEditForm(null);
    setShowCcBcc(false);
    onEditCancelled?.();
  }, [draft, editForm, onEditCancelled]);

  const toggleCcBcc = useCallback(() => setShowCcBcc((v) => !v), []);

  const buildUpdatePayload = useCallback(
    (form: EditForm) => ({
      draftId,
      toolkitName,
      accountLabel,
      ...editFormToUpdatePayload(form),
    }),
    [draftId, toolkitName, accountLabel]
  );

  const handleSave = useCallback(async () => {
    if (!editForm || updateDraft.isPending) return;
    try {
      await updateDraft.mutateAsync(buildUpdatePayload(editForm));
      toast.success('Draft updated');
      onSaved?.();
    } catch (e) {
      const message =
        e instanceof HttpError ? e.message : 'Failed to update draft';
      toast.error(message);
    }
  }, [editForm, updateDraft, buildUpdatePayload, onSaved]);

  const handleSend = useCallback(async () => {
    if (sendState !== 'idle') return;
    setSendState('sending');
    try {
      await sendDraft.mutateAsync({ draftId, toolkitName, accountLabel });
      setSendState('sent');
      onSent?.();
      trackEvent('EMAIL_SENT_VIA_INTEGRATION', {
        toolkit_name: toolkitName,
        edited: false,
      });
    } catch (e) {
      const message =
        e instanceof HttpError ? e.message : 'Failed to send email';
      toast.error(message);
      setSendState('idle');
    }
  }, [draftId, toolkitName, accountLabel, sendState, sendDraft, onSent]);

  const handleSendFromEdit = useCallback(async () => {
    if (!editForm || sendState !== 'idle' || updateDraft.isPending) return;
    setSendState('sending');
    try {
      await updateDraft.mutateAsync(buildUpdatePayload(editForm));
      await sendDraft.mutateAsync({ draftId, toolkitName, accountLabel });
      setSendState('sent');
      onSent?.();
      trackEvent('EMAIL_SENT_VIA_INTEGRATION', {
        toolkit_name: toolkitName,
        edited: true,
      });
    } catch (e) {
      const message =
        e instanceof HttpError ? e.message : 'Failed to send email';
      toast.error(message);
      setSendState('idle');
    }
  }, [
    editForm,
    sendState,
    updateDraft,
    sendDraft,
    buildUpdatePayload,
    draftId,
    toolkitName,
    accountLabel,
    onSent,
  ]);

  return {
    isAwaitingAutoEdit,
    isEditing,
    editForm,
    setEditForm,
    showCcBcc,
    toggleCcBcc,
    sendState,
    isUnavailable,
    isAccountNotFound,
    loadState,
    loadError,
    isSaving,
    draft,
    refetch: draftQuery.refetch,
    editFooterRef,
    IntegrationIcon,
    fromLine,
    handleStartEdit,
    handleCancelEdit,
    handleSave,
    handleSend,
    handleSendFromEdit,
  };
}
